import React from 'react';
import PropTypes from 'prop-types';
import { Button, Modal, Spacing, Text } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';

const DISMISS_EVENT = 'searchwidget:passenger-info-dismissed';
const INFO_MODAL_KEY = 'child_in_arms_info';

/**
 * ChildInArmsInfoModal component. Renders the funnel-owned info modal triggered
 * by the search widget's `Criança de colo` link. Any dismissal (Entendido CTA,
 * X close, overlay click) dispatches `searchwidget:passenger-info-dismissed` on
 * `document` so the widget reopens the passenger picker with prior selections.
 * @param {object} props - isOpen, onClose.
 * @param {boolean} props.isOpen - Whether the modal is currently shown.
 * @param {Function} props.onClose - Parent handler that flips `isOpen` to false.
 * @returns {JSX|null} - React component or null when closed.
 */
const ChildInArmsInfoModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation('general');

  if (!isOpen) return null;

  /**
   * Dispatch the dismiss event so the search widget reopens its passenger
   * picker, then close the modal locally. Used by both the X / overlay close
   * path and the CTA so any dismissal reopens the picker.
   */
  const handleClose = () => {
    document.dispatchEvent(
      new CustomEvent(DISMISS_EVENT, { detail: { infoModalKey: INFO_MODAL_KEY } }),
    );
    onClose();
  };

  return (
    <Modal
      defaultOpened
      onCloseModal={handleClose}
      aria-label={t('child_in_arms_info.title')}
      rounded="M"
      responsiveSize="auto"
      contentHeight="auto"
      removeContentPadding
      footer={
        <div
          style={{
            marginTop: '40px',
            padding: '0px 0px 45px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <Button
            text={t('child_in_arms_info.cta')}
            variant="accent"
            className="confirm"
            padding="XL"
            mobilePadding="XL"
            borderRadius="L"
            mobileBorderRadius="L"
            onClick={handleClose}
          />
        </div>
      }
    >
      <div
        style={{
          marginTop: '0px',
          marginBottom: '0px',
          paddingTop: '40px',
          paddingBottom: '0px',
          paddingLeft: '30px',
          paddingRight: '30px',
        }}
      >
        <Spacing vertical size="M" responsiveSize="S" textAlign="center">
          <Text mobileSize="S">{t('child_in_arms_info.legal_basis')}</Text>
          <Text mobileSize="S" weight="bold">
            {t('child_in_arms_info.ticket_required')}
          </Text>
          <Text mobileSize="S">{t('child_in_arms_info.documents')}</Text>
          <Text mobileSize="S" weight="bold">
            {t('child_in_arms_info.data_capture')}
          </Text>
        </Spacing>
      </div>
    </Modal>
  );
};

ChildInArmsInfoModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ChildInArmsInfoModal;
